/*
 * VH-83 — Print + Download PDF actions for the Comprehensive Health Analysis.
 *
 * - handlePrint(): tags <body> with `printing-comprehensive` so the
 *   scoped @media print stylesheet (print.css) can hide the SPA chrome,
 *   then calls window.print(). The body class is cleared on `afterprint`.
 * - handleDownloadPdf(): rasterises the supplied report DOM via
 *   html2canvas-pro, then slices the canvas across A4/Letter PDF pages with
 *   jsPDF (lifted from pages/NarrativeReport.jsx).
 *
 * html2canvas-pro replaces html2canvas because the latter cannot parse the
 * oklch() colours Tailwind 4 emits — see RecipePrintActions.jsx for the full
 * write-up.
 *
 * No backend calls — the user is already authenticated to view the
 * report on screen, so generating from that DOM is implicitly authorised.
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { withLightTheme } from '../../utils/captureLight'
import {
  PrinterIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import {
  getPaperSize,
  applyPrintPaper,
  clearPrintPaper,
} from '../../utils/printPaper'

const PDF_MARGIN_MM = 10

function formatDateForFilename(value) {
  if (!value) return new Date().toISOString().split('T')[0]
  try {
    const d = new Date(value)
    if (Number.isNaN(d.getTime())) return new Date().toISOString().split('T')[0]
    return d.toISOString().split('T')[0]
  } catch {
    return new Date().toISOString().split('T')[0]
  }
}

export default function ComprehensiveAnalysisActions({
  report,
  reportRef,
  status,
}) {
  const { t, i18n } = useTranslation()
  const [isExporting, setIsExporting] = useState(false)

  const isCompleted = (status || '').toLowerCase() === 'completed'
  const disabled = !isCompleted || isExporting

  // Restore body state if the user navigates away from the print preview.
  useEffect(() => {
    const handleAfterPrint = () => {
      document.body.classList.remove('printing-comprehensive')
      document.body.removeAttribute('data-print-paper')
      clearPrintPaper()
    }
    window.addEventListener('afterprint', handleAfterPrint)
    return () => {
      window.removeEventListener('afterprint', handleAfterPrint)
      // Unmounting mid-print would otherwise strand the injected @page rule
      // and leak the chosen size into unrelated prints.
      clearPrintPaper()
    }
  }, [])

  const handlePrint = () => {
    if (!isCompleted) return
    const paper = getPaperSize(i18n.language)
    document.body.classList.add('printing-comprehensive')
    document.body.setAttribute('data-print-paper', paper)
    // The @page size has to be injected — it cannot be selector-scoped in CSS.
    applyPrintPaper(paper)
    // Defer to next tick so the class takes effect before the dialog opens.
    setTimeout(() => {
      window.print()
    }, 0)
  }

  const handleDownloadPdf = async () => {
    if (!isCompleted || !reportRef?.current) return
    setIsExporting(true)
    try {
      // Lazy-load the PDF libs so they don't sit in the main bundle.
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import('html2canvas-pro'),
        import('jspdf'),
      ])

      // VH-133: capture in light colours whatever theme is on screen — this
      // report gets forwarded to a doctor, and a black page is not that.
      const canvas = await withLightTheme(reportRef.current, () =>
        html2canvas(reportRef.current, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
        })
      )

      const paper = getPaperSize(i18n.language)
      const pdf = new jsPDF('p', 'mm', paper)
      const pdfWidth = pdf.internal.pageSize.getWidth()
      const pdfHeight = pdf.internal.pageSize.getHeight()
      const imgWidth = canvas.width
      const imgHeight = canvas.height

      const usableWidth = pdfWidth - PDF_MARGIN_MM * 2
      const usableHeight = pdfHeight - PDF_MARGIN_MM * 2
      const ratio = usableWidth / imgWidth
      const sliceHeightPx = usableHeight / ratio
      const totalPages = Math.max(1, Math.ceil(imgHeight / sliceHeightPx))

      for (let page = 0; page < totalPages; page++) {
        if (page > 0) pdf.addPage()

        const sourceY = page * sliceHeightPx
        const sourceHeight = Math.min(sliceHeightPx, imgHeight - sourceY)

        // Slice the source canvas into a per-page canvas.
        const pageCanvas = document.createElement('canvas')
        pageCanvas.width = imgWidth
        pageCanvas.height = sourceHeight
        const ctx = pageCanvas.getContext('2d')
        ctx.fillStyle = '#ffffff'
        ctx.fillRect(0, 0, imgWidth, sourceHeight)
        ctx.drawImage(
          canvas,
          0, sourceY, imgWidth, sourceHeight,
          0, 0, imgWidth, sourceHeight
        )

        const pageImgData = pageCanvas.toDataURL('image/jpeg', 0.92)
        pdf.addImage(
          pageImgData,
          'JPEG',
          PDF_MARGIN_MM,
          PDF_MARGIN_MM,
          usableWidth,
          sourceHeight * ratio
        )
      }

      // Footer on the last page.
      pdf.setFontSize(8)
      pdf.setTextColor(128, 128, 128)
      pdf.text(
        `VitaHub — ${new Date().toLocaleDateString()}`,
        pdfWidth / 2,
        pdfHeight - 4,
        { align: 'center' }
      )

      const dateStr = formatDateForFilename(report?.created_at)
      const reportId = report?.id ?? 'report'
      pdf.save(`health-analysis-${dateStr}-${reportId}.pdf`)
    } catch (err) {
       
      console.error('PDF export failed', err)
      toast.error(t('reports.pdf_failed', 'Could not generate PDF. Please try again.'))
    } finally {
      setIsExporting(false)
    }
  }

  const tooltip = !isCompleted ? t('reports.print_disabled') : undefined

  return (
    <div
      className="analysis-actions flex items-center gap-2"
      data-testid="comprehensive-analysis-actions"
    >
      <button
        type="button"
        onClick={handlePrint}
        disabled={disabled}
        title={tooltip}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
          disabled
            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            : 'bg-surface-container text-gray-700 border-gray-300 hover:bg-gray-50'
        }`}
        aria-label={t('reports.print')}
      >
        <PrinterIcon className="h-4 w-4" />
        <span>{t('reports.print')}</span>
      </button>
      <button
        type="button"
        onClick={handleDownloadPdf}
        disabled={disabled}
        title={tooltip}
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border transition-colors ${
          disabled
            ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
            : 'bg-purple-600 text-on-neutral border-purple-600 hover:bg-purple-700'
        }`}
        aria-label={t('reports.download_pdf')}
      >
        {isExporting ? (
          <>
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
            <span>{t('reports.pdf_generating')}</span>
          </>
        ) : (
          <>
            <ArrowDownTrayIcon className="h-4 w-4" />
            <span>{t('reports.download_pdf')}</span>
          </>
        )}
      </button>
    </div>
  )
}
